import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FulfillmentActionItemCode } from '@ekhodealer/ekho-common';

import type { MarkBackfillAccessoriesRequestedResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * API function to mark backfill accessories as requested
 * @param servicesProviderId - The services provider ID
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const markBackfillAccessoriesRequested = async (
	servicesProviderId: string,
	orderId: string,
): Promise<MarkBackfillAccessoriesRequestedResponse> => {
	const response = await apiClient.post<MarkBackfillAccessoriesRequestedResponse>(
		ordersEndpoints.markBackfillAccessoriesRequested(servicesProviderId, orderId),
		{}, // Empty body as per backend schema
	);
	return response.data;
};

/**
 * Hook to mark backfill accessories as requested
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useMarkBackfillAccessoriesRequested = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to mark backfill accessories as requested without an order ID.',
				);
			}

			return markBackfillAccessoriesRequested(servicesProviderId, orderId);
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, FulfillmentActionItemCode.MARK_BACKFILL_ACCESSORIES_REQUESTED);
		},
	});
};
